import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styled from '@emotion/styled';
import { Alert } from '@lightspeed/flame/Alert';
import { Autocomplete } from '@lightspeed/flame/Autocomplete';
import { Box, Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { Divider } from '@lightspeed/flame/Divider';
import { ModalHeader as FlameModalHeader } from '@lightspeed/flame/Modal';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { addShopAccess, clearShopAccess } from 'pages/store/actions/employeeShops';
import { getStore } from 'pages/store/selectors/employeeShops';

import { ShopAutoComplete } from '../../types/shops';

const StyledTextLink = styled(TextLink)`
  text-decoration: underline;
`;

interface Props {
  readOnly?: boolean;
}

const ModalHeader: React.FC<Props> = ({ readOnly = false }) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = React.useState('');

  const { error, shops } = useSelector(getStore);
  const allowedShops = shops.filter((shop) => !shop.hasAccess);
  const employeeShops = shops.filter((shop) => shop.hasAccess);

  const handleOnChangeInput = React.useCallback((value, meta) => {
    const newInputValue = ['set-value', 'menu-close'].includes(meta.action) ? '\r' : value;
    setInputValue(newInputValue);
    return newInputValue;
  }, []);

  const handleOnChangeAutocomplete = (newShop: ShopAutoComplete) =>
    dispatch(addShopAccess(newShop.value));

  const handleOnClickRemoveAll = () => dispatch(clearShopAccess());

  const totalEmployeeShops = employeeShops.length;

  const translate = (text: string) => {
    // Cannot use window.Localize?.translate because of return type mismatch
    if (window.Localize) {
      return window.Localize.translate(text);
    }
    return text;
  };

  return (
    <>
      <FlameModalHeader>Location access</FlameModalHeader>
      <Box p={2}>
        {error && (
          <Alert data-test="unable-to-save" marginBottom={3} py={3} type="danger">
            Unable to save employee&apos;s location access. Try again.
          </Alert>
        )}
        {!readOnly && (
          <Autocomplete
            aria-label="autocomplete-shop-input"
            data-test="shops-search-field"
            className="notranslate"
            inputId="shop-input"
            inputValue={inputValue}
            name="shop-input"
            noOptionsMessage={() => translate('No options')}
            onChange={handleOnChangeAutocomplete}
            onInputChange={handleOnChangeInput}
            options={allowedShops.map((shop) => ({ value: shop.id, label: shop.name }))}
            placeholder={translate('Select a location')}
          />
        )}
      </Box>
      <Divider />
      {!!totalEmployeeShops && (
        <Box>
          <Flex justifyContent="space-between" alignItems="center" py={2} px={3}>
            <Text size="small" color={theme.colors['gray-600']} data-test="shop-counter-modal">
              <var>{totalEmployeeShops}</var> of <var>{shops.length}</var> locations
            </Text>
            {!readOnly && (
              <StyledTextLink
                color={theme.colors['gray-600']}
                onClick={handleOnClickRemoveAll}
                size="small"
              >
                Remove all locations
              </StyledTextLink>
            )}
          </Flex>
          <Divider />
        </Box>
      )}
    </>
  );
};

export default ModalHeader;
